import React, { useMemo, useState } from 'react';
import { BarChart2, PieChart, TrendingUp, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  WinLossDonutChart,
  CategoryBarChart,
  RollingWinRateChart,
  HoldingTimeProfitScatter,
} from './charts';

/**
 * @typedef {Object} Trade
 * @property {number} profit - Trade profit/loss
 * @property {string} [symbol] - Trading symbol
 * @property {string} [direction] - Trade direction
 * @property {string} [asset_type] - Asset type
 * @property {string} [orderType] - Order type
 * @property {string} [openTime] - Open time
 * @property {string} [closeTime] - Close time
 * @property {string} [trade_date] - Trade date
 */

/**
 * Card wrapper component for consistent styling
 */
const ChartCard = ({ children, title, icon: Icon, className = '' }) => (
  <div className={`bg-card text-card-foreground rounded-lg shadow-sm border border-border p-4 ${className}`}>
    {(title || Icon) && (
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
        {title && <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>}
      </div>
    )}
    {children}
  </div>
);

/**
 * Analytics Dashboard
 * Professional prop-firm style dashboard with multiple chart types
 *
 * @param {Object} props
 * @param {Trade[]} props.trades - Array of trade objects
 * @param {string} [props.className=''] - Additional CSS classes
 *
 * @example
 * <AnalyticsDashboard trades={trades} />
 */
const AnalyticsDashboard = ({ trades = [], className = '' }) => {
  // State for configurable options
  const [categoryGroupBy, setCategoryGroupBy] = useState('symbol');
  const [rollingWindow, setRollingWindow] = useState(20);

  // Filter trades with valid data for different charts
  const validTrades = useMemo(() => {
    return trades.filter(trade => trade && typeof trade.profit !== 'undefined');
  }, [trades]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (!validTrades.length) return null;

    const wins = validTrades.filter(t => Number(t.profit) > 0.01);
    const losses = validTrades.filter(t => Number(t.profit) < -0.01);
    const totalProfit = validTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);

    return {
      totalTrades: validTrades.length,
      wins: wins.length,
      losses: losses.length,
      winRate: (wins.length / validTrades.length) * 100,
      totalProfit,
      avgProfit: totalProfit / validTrades.length,
    };
  }, [validTrades]);

  // Empty state
  if (!validTrades.length) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${className}`}>
        <div className="text-center text-muted-foreground">
          <BarChart2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No trade data available</p>
          <p className="text-sm">Add trades to see your analytics dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Top Row: Win/Loss Donut + Category Bar Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Win/Loss Distribution Donut */}
        <ChartCard icon={PieChart} title="Win/Loss Distribution">
          <WinLossDonutChart
            trades={validTrades}
            title=""
            height={280}
          />
        </ChartCard>

        {/* Category Bar Chart with Selector */}
        <ChartCard icon={BarChart2} title="Performance by Category">
          <div className="flex justify-end mb-2">
            <Select value={categoryGroupBy} onValueChange={setCategoryGroupBy}>
              <SelectTrigger className="w-[140px] h-8 text-sm bg-muted border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="symbol">Symbol</SelectItem>
                <SelectItem value="direction">Direction</SelectItem>
                <SelectItem value="assetType">Asset Type</SelectItem>
                <SelectItem value="orderType">Order Type</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <CategoryBarChart
            trades={validTrades}
            groupBy={categoryGroupBy}
            title=""
            height={240}
            maxCategories={8}
          />
        </ChartCard>
      </div>

      {/* Middle Row: Rolling Win Rate */}
      <ChartCard icon={TrendingUp} title="Rolling Win Rate">
        <div className="flex justify-end mb-2">
          <Select value={String(rollingWindow)} onValueChange={(v) => setRollingWindow(Number(v))}>
            <SelectTrigger className="w-[140px] h-8 text-sm bg-muted border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10-Trade Window</SelectItem>
              <SelectItem value="20">20-Trade Window</SelectItem>
              <SelectItem value="50">50-Trade Window</SelectItem>
              <SelectItem value="100">100-Trade Window</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <RollingWinRateChart
          trades={validTrades}
          window={rollingWindow}
          title=""
          height={280}
        />
      </ChartCard>

      {/* Bottom Row: Holding Time Scatter */}
      <ChartCard icon={Clock} title="Holding Time vs Profit">
        <HoldingTimeProfitScatter
          trades={validTrades}
          title=""
          height={280}
        />
      </ChartCard>
    </div>
  );
};

export default AnalyticsDashboard;

/**
 * ========================================
 * USAGE EXAMPLES
 * ========================================
 *
 * 1. Basic Usage - Full Dashboard
 * --------------------------------
 * import AnalyticsDashboard from './AnalyticsDashboard';
 *
 * const MyPage = () => {
 *   const [trades, setTrades] = useState([]);
 *
 *   useEffect(() => {
 *     fetchTrades().then(setTrades);
 *   }, []);
 *
 *   return <AnalyticsDashboard trades={trades} />;
 * };
 *
 *
 * 2. Individual Chart Components
 * --------------------------------
 * import {
 *   WinLossDonutChart,
 *   CategoryBarChart,
 *   RollingWinRateChart,
 *   VolumeWinRateComboChart,
 *   HoldingTimeProfitScatter,
 * } from './charts';
 *
 * // Donut Chart
 * <WinLossDonutChart
 *   trades={trades}
 *   title="Win Rate Distribution"
 *   height={350}
 * />
 *
 * // Category Bar Chart (groupBy: 'symbol' | 'direction' | 'assetType' | 'orderType')
 * <CategoryBarChart
 *   trades={trades}
 *   groupBy="symbol"
 *   title="Wins vs Losses by Symbol"
 *   height={400}
 *   maxCategories={10}
 * />
 *
 * // Rolling Win Rate Line Chart
 * <RollingWinRateChart
 *   trades={trades}
 *   window={20}
 *   xAxis="index"    // or "date"
 *   title="Rolling Win Rate (20-Trade Window)"
 *   height={300}
 * />
 *
 * // Volume + Win Rate Combo Chart
 * <VolumeWinRateComboChart
 *   trades={trades}
 *   title="Weekly Performance"
 *   height={350}
 *   maxWeeks={12}
 * />
 *
 * // Holding Time vs Profit Scatter
 * <HoldingTimeProfitScatter
 *   trades={trades}
 *   title="Trade Duration Analysis"
 *   height={350}
 *   maxHoldingMinutes={480}  // Optional: filter to trades < 8 hours
 * />
 *
 *
 * 3. Required Trade Data Structure
 * --------------------------------
 * Each trade object should have:
 * {
 *   profit: number,           // Required: P/L amount
 *   symbol: string,           // Optional: e.g., "EUR/USD"
 *   direction: string,        // Optional: "BUY" or "SELL"
 *   asset_type: string,       // Optional: "forex", "crypto", etc.
 *   orderType: string,        // Optional: "market", "limit", etc.
 *   openTime: string,         // Optional: ISO date string
 *   closeTime: string,        // Optional: ISO date string
 *   trade_date: string,       // Optional: ISO date string
 * }
 */
